import { RetailerAccount, User } from '@promoboxx/graphql-gateway-types'
import { LDSingleKindContext, useLDClient } from 'launchdarkly-react-client-sdk'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import AppConfig from '@src/config/AppConfig'
import getPbxxId from '@src/lib/getPbxxId'
import useCurrentAuth from '@src/lib/useCurrentAuth'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'

import analytics, { IdentifiedUser } from './index'

const AnalyticsLoader: React.FC<{}> = () => {
  const { pathname, search } = useLocation()
  const ldClient = useLDClient()

  const refererRef = useRef<string>()
  const { currentUser: user, retailerAccounts } = useCurrentAuth()
  const { currentRetailerAccount } = useCurrentRetailerAccount()

  useEffect(() => {
    if (user) {
      const identifiedUser = getStandardizedUser(
        user,
        retailerAccounts,
        currentRetailerAccount,
      )
      analytics.identify(identifiedUser)
      const toNumericIds = (ids: string[]) =>
        ids.map(Number).filter(Number.isFinite)

      const brandIdsNumeric = toNumericIds(identifiedUser.brandIds)
      const retailerAccountIdsNumeric = toNumericIds(
        identifiedUser.retailerAccountIds,
      )
      const retailerIdsNumeric = toNumericIds(identifiedUser.retailerIds)

      const launchDarklyContext: LDSingleKindContext = {
        kind: 'user',
        key: identifiedUser.email,
        // Covered via `getStandardizedUser`
        // anonymous: false,
        // name: identifiedUser.name,
        ...identifiedUser,
        brandIds: brandIdsNumeric,
        retailerAccountIds: retailerAccountIdsNumeric,
        retailerIds: retailerIdsNumeric,
      }

      ldClient?.identify(launchDarklyContext)
    } else {
      analytics.identify({
        id: '-1',
        email: 'anonymous@promoboxx.com',
        createdAt: new Date().toISOString(),
        type: 'Anonymous',
        env: AppConfig.ENV,
        app: 'retailer',
        uuid: '',
        timeZone: '',
        timeZoneAbbr: '',
        name: '',
        firstName: '',
        lastName: '',
        languagePreference: 'en-us',
        roles: [],
        industryList: [],
        brandNames: [],
        brandIds: [],
        retailerAccountNames: [],
        retailerAccountIds: [],
        retailerIds: [],
        retailerContentDiscovery: false,
        enableBrandRequests: false,
        enablePicInDiscoverBrands: false,
        enableIndustrySuggestions: false,
        isFacebookAuthenticated: false,
        anonymous: true,
        pbxxId: getPbxxId(),
      })
    }

    // clear any stale data to prevent leaks, segment will hold onto stale data if we don't reset it
    return () => {
      analytics.reset()
    }
  }, [user, retailerAccounts, currentRetailerAccount, ldClient])

  useEffect(() => {
    analytics.setCommonInfo({
      retailerAccountId: currentRetailerAccount?.id,
      retailerAccountName: currentRetailerAccount?.name,
    })
  }, [currentRetailerAccount])

  // Track page loads.
  useEffect(() => {
    analytics.page({
      type: 'Page View',
      path: pathname,
      referer: refererRef.current,
      title: document.title,
      url: window.location.href,
      search,
    })

    refererRef.current = pathname
  }, [pathname, search])

  // Easy tracking of elements with `data-track-identifier` attribute, or `data-testid` since anything we test we probably want to track.
  useEffect(() => {
    function findClosest(element: unknown) {
      if (element instanceof Element) {
        return element.closest('[data-track-identifier], [data-testid]')
      }

      return null
    }

    function isMuiIcon(element: unknown): element is SVGElement {
      return Boolean(
        element instanceof SVGElement &&
          element.getAttribute('data-testid')?.endsWith('Icon'),
      )
    }

    function listener(event: MouseEvent) {
      let elementWithTrackingAttribute = findClosest(event.target)

      // We want to allow tracking of svg elements, but mui icons come with
      // their own data-testid that isn't useful for tracking.
      if (isMuiIcon(elementWithTrackingAttribute)) {
        elementWithTrackingAttribute = findClosest(
          elementWithTrackingAttribute.parentNode,
        )
      }

      if (!elementWithTrackingAttribute) {
        return
      }

      const identifier =
        elementWithTrackingAttribute.getAttribute('data-track-identifier') ||
        elementWithTrackingAttribute.getAttribute('data-testid')

      if (!identifier) {
        return
      }

      analytics.track({
        event: 'Click',
        type: identifier,
      })
    }

    document.addEventListener('click', listener, {
      capture: true,
    })

    return () => {
      document.removeEventListener('click', listener, {
        capture: true,
      })
    }
  }, [])

  return null
}

export default AnalyticsLoader

// this function is used to standardize the user object for analytics based off the old analytics redux implementation
const getStandardizedUser = (
  user: User,
  retailerAccounts?: RetailerAccount[],
  currentRetailerAccount?: RetailerAccount | null,
) => {
  let brandNames: string[] = []
  let brandIds: string[] = []
  let industryList: string[] = []
  let retailerAccountNames: string[] = []
  let retailerAccountIds: string[] = []
  let retailerIds: string[] = []

  let retailerContentDiscovery = false
  let enableBrandRequests = false
  let enablePicInDiscoverBrands = false
  let enableIndustrySuggestions = false

  if (retailerAccounts) {
    retailerAccounts.forEach((retailerAccount) => {
      if (retailerAccount) {
        const retailerAccountName = retailerAccount?.name || ''
        const retailerAccountId = String(retailerAccount.id)
        if (!retailerAccountIds.includes(retailerAccountId)) {
          retailerAccountNames.push(retailerAccountName)
          retailerAccountIds.push(retailerAccountId)
        }
        if (retailerAccount.retailers) {
          retailerAccount.retailers.forEach((retailer) => {
            if (retailer && retailer.brand) {
              const brandName = retailer?.brand?.name || ''
              const brandId = String(retailer.brand.id)
              // This whole snippet was written by GitHub Copilot
              retailer.brand.industry_list &&
                retailer.brand.industry_list.forEach((industry) => {
                  if (!industryList.includes(industry)) {
                    industryList.push(industry)
                  }
                })
              const retailerId = String(retailer.id)
              if (!brandIds.includes(brandId)) {
                brandNames.push(brandName)
                brandIds.push(brandId)
              }
              if (!retailerIds.includes(retailerId)) {
                retailerIds.push(retailerId)
              }
            }
          })
        }
      }
    })
  }

  let isFacebookAuthenticated = false

  if (currentRetailerAccount) {
    retailerContentDiscovery =
      currentRetailerAccount.retailers?.some(
        (retailer) => retailer.brand?.settings?.brand_content_discovery,
      ) || false
    enableBrandRequests =
      currentRetailerAccount.retailers?.every(
        (retailer) => retailer.brand?.brand_requests_enabled,
      ) || false
    enablePicInDiscoverBrands =
      currentRetailerAccount.retailers?.every(
        (retailer) =>
          retailer.brand?.settings
            ?.retailer_connection_requests_pic_suggestions_enabled,
      ) || false
    enableIndustrySuggestions =
      currentRetailerAccount.retailers?.every(
        (retailer) =>
          retailer.brand?.settings
            ?.retailer_connection_requests_industry_suggestions_enabled,
      ) || false

    isFacebookAuthenticated =
      currentRetailerAccount.facebook_pages?.some(
        (x) => x.remote_token_status?.is_valid,
      ) || false
  }

  const identifiedUser: IdentifiedUser = {
    id: user?.id || '',
    email: user?.email || '',
    createdAt: user?.created_at || '',
    type: user?.type || '',
    env: AppConfig.ENV,
    app: 'retailer',
    uuid: user?.uuid || '',
    timeZone: user?.time_zone || '',
    timeZoneAbbr: user?.time_zone_abbr || '',
    name: `${user?.first_name} ${user?.last_name}`,
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    languagePreference: user?.language_preference || 'en-us' || '',
    roles: user?.user_roles || [],
    industryList,
    brandNames,
    brandIds,
    retailerAccountNames,
    retailerAccountIds,
    retailerIds,
    retailerContentDiscovery,
    enableBrandRequests,
    enablePicInDiscoverBrands,
    enableIndustrySuggestions,
    isFacebookAuthenticated,
    anonymous: false,
    pbxxId: getPbxxId(),
  }
  return identifiedUser
}
